import React, { useCallback } from 'react';

import { getLists } from '@vlabs/api/artemy/client';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';
import validate from '@vlabs/shared/validators';
import {
  Control, Margin, Grid, GridRow, GridCol,
} from '@vlabs/uikit';
import type { TOption } from '@vlabs/uikit/types';
import { useForm } from 'react-hook-form';

import st from './CreateHandlerForm.module.sass';
import { useCreateHandlerTranslation } from './i18n';
import { OwnerDepartmentSelect } from './OwnerDepartmentSelect';

export interface BiometricIdentificationFormValues {
  description?: string;
  listId: string;
  owner_department_id: string;
}

interface BiometricIdentificationFormProps {
  onSubmit: (values: BiometricIdentificationFormValues) => void;
}
function BiometricIdentificationForm({
  onSubmit,
}: BiometricIdentificationFormProps): React.JSX.Element {
  const { t } = useCreateHandlerTranslation();
  const {
    register, control, handleSubmit, setValue, formState: { errors },
  } = useForm<BiometricIdentificationFormValues>();

  const loadOptions = useCallback(async (inputValue: string): Promise<TOption[]> => {
    const { lists = [] } = await getLists({
      page_size: 100,
      ...(inputValue ? { user_data: inputValue } : {}),
    });

    return lists.map(({ list_id, user_data }) => ({
      label: user_data || list_id,
      value: list_id,
    }));
  }, []);

  return (
    <form
      data-testid="modal.biometricIdentification"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid>
        <GridRow>
          <GridCol>
            <Margin display="block">
              <span className={st.Description}>
                {t('form.biometric_description')}
              </span>
            </Margin>
            <Margin display="block">
              <div className="Body-2">{t('form.name')}</div>
              <Control.Input
                {...register('description', {
                  maxLength: validate.maxLength(128),
                  required: validate.required(),
                })}
                errors={errors}
              />
            </Margin>
            <Margin display="block">
              <OwnerDepartmentSelect
                control={control}
                errors={errors}
                label={t('form.owner_department')}
                setValue={setValue}
              />
            </Margin>
            <Margin display="block">
              <ControllableValueSelect
                cacheOptions
                control={control}
                defaultOptions
                errors={errors}
                id="listId"
                isAsync
                isClearable
                label={t('form.list')}
                loadOptions={loadOptions}
                name="list_id"
                rules={{ required: validate.required() }}
              />
            </Margin>
          </GridCol>
        </GridRow>
      </Grid>
      <Margin display="block">
        <Control.Button
          fullWidth
          type="submit"
        >
          {t('form.create')}
        </Control.Button>
      </Margin>
    </form>
  );
}

export { BiometricIdentificationForm };
